// Shared collapsible sidebar nav — identical behavior across the doctor, patient, and command portals.
// The collapse logic + persistence live here so the three Shells don't fork it; each Shell still owns
// its own nav links (they differ per portal) and passes them as children.
//
// Behavior:
//  - OPEN: full nav (icons + labels + category headers). CLOSED: icons only, evenly spaced (the
//    collapsed visuals are CSS in styles.css, keyed off `.pt-shell.nav-collapsed`).
//  - An always-visible circular <button> toggle (top of the nav) collapses/expands; the chevron
//    points in when open, out when collapsed (FB request — matches Luan's .sidebar-toggle). Clicking
//    the empty collapsed rail also expands (icon clicks still navigate; we ignore clicks inside an
//    <a>/<button>).
//  - The open/closed choice persists in localStorage.
import { useCallback, useEffect, useState, type ReactNode } from "react";

/**
 * Collapse state + persistence. `storageKey` namespaces it per portal. The nav also AUTO-collapses
 * when the viewport is narrower than `autoCollapseBelow` (a small window forces the icon rail — which
 * stays visible, never disappears) without overwriting the user's saved choice; widening restores it.
 * SSR / no-storage safe.
 */
export function useNavCollapse(
  storageKey = "cc-nav-collapsed",
  autoCollapseBelow = 1024,
): {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
} {
  const [userCollapsed, setUserCollapsedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  const [narrow, setNarrow] = useState<boolean>(() => {
    try {
      return window.innerWidth < autoCollapseBelow;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < autoCollapseBelow);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, [autoCollapseBelow]);

  const setCollapsed = useCallback(
    (v: boolean) => {
      setUserCollapsedState(v);
      try {
        localStorage.setItem(storageKey, v ? "1" : "0");
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );
  // A narrow viewport forces collapse; otherwise honor the user's saved choice.
  const collapsed = narrow || userCollapsed;
  // Mirror onto <body> so chrome outside the sidebar (e.g. the top-bar hamburger) can react.
  useEffect(() => {
    document.body.classList.toggle("cc-nav-collapsed", collapsed);
    return () => document.body.classList.remove("cc-nav-collapsed");
  }, [collapsed]);
  const toggle = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);
  return { collapsed, setCollapsed, toggle };
}

const Chevron = ({ collapsed }: { collapsed: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {collapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}
  </svg>
);

/** The <aside> sidebar: chevron toggle + click-empty-to-expand + the portal's nav as children. */
export function Sidebar({
  collapsed,
  onToggle,
  onExpand,
  children,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onExpand: () => void;
  children: ReactNode;
}): JSX.Element {
  // Expand when the empty sidebar area is clicked while collapsed — but never hijack a nav link/icon
  // click (those land inside an <a>/<button> and must navigate) or the chevron (handled separately).
  function onAreaClick(e: React.MouseEvent<HTMLElement>) {
    if (!collapsed) return;
    const t = e.target as HTMLElement;
    if (t.closest("a, .pt-navlink, .pt-nav-chevron")) return;
    onExpand();
  }
  return (
    <>
      <aside className="pt-sidebar" onClick={onAreaClick}>
        {children}
      </aside>
      {/* The toggle is a SIBLING of the sidebar (not a child) so it can sit ON the sidebar's right
          border line: the sidebar is a sticky scroll container (overflow-y:auto) and would clip an
          edge button. Fixed-positioned on the column boundary; stays in view on scroll. */}
      <button
        type="button"
        className="pt-nav-chevron"
        onClick={onToggle}
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        aria-expanded={!collapsed}
      >
        <Chevron collapsed={collapsed} />
      </button>
    </>
  );
}
